import { Banner, Placeholder, Spinner } from '@telegram-apps/telegram-ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ExternalLink } from 'lucide-react'
import { Page } from 'shared/components/Page'
import { TezIdDialog } from 'shared/components/TezIdDialog'
import { LotImageSlider } from 'shared/components/LotImageSlider'
import { ShareLotButton } from 'shared/components/ShareLotButton'
import { useMobileTrpc } from 'shared/api'
import type { MobileOutputs } from 'shared/api'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import type { TranslationPaths } from 'shared/lib/i18n'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'
import { formatDateTime } from 'shared/lib/utils'
import { useAuthStore } from 'shared/store/auth'
import { PurchasePanel, type PurchaseLine } from './PurchasePanel'
import { SizeGrid } from './SizeGrid'
import { StockCounters } from './StockCounters'

type Inventory = MobileOutputs['lots']['GetById']['inventory']

type PurchaseOutcome =
	| { type: 'success'; message: string }
	| { type: 'error'; message: string }

const LOT_STATE_KEYS = {
	draft: 'status.lot.draft',
	collecting: 'status.lot.collecting',
	paused: 'status.lot.paused',
	ready: 'status.lot.ready',
	purchased: 'status.lot.purchased',
	cancelled: 'status.lot.cancelled',
} as const satisfies Record<string, TranslationPaths>

/** Drop selection entries no longer buyable (slot taken, stock reduced). */
function reconcileSelection(
	selection: Map<string, number>,
	inventory: Inventory,
): Map<string, number> {
	const next = new Map<string, number>()
	if (inventory.type === 'slots') {
		for (const slot of inventory.items)
			if (slot.status === 'free' && selection.has(slot.id)) next.set(slot.id, 1)
	} else {
		for (const item of inventory.items) {
			const clamped = Math.min(
				selection.get(item.id) ?? 0,
				item.availableQuantity,
			)
			if (clamped > 0) next.set(item.id, clamped)
		}
	}
	return next
}

/** The unified purchase lines the panel drives `Buy` with, for either mode. */
function buildLines(
	selection: Map<string, number>,
	inventory: Inventory,
): PurchaseLine[] {
	if (inventory.type === 'slots')
		return inventory.items
			.filter((s) => s.status === 'free' && selection.has(s.id))
			.map((s) => ({ inventoryId: s.id, name: s.name, quantity: 1 }))
	return inventory.items
		.map((i) => ({
			inventoryId: i.id,
			name: i.name,
			quantity: Math.min(selection.get(i.id) ?? 0, i.availableQuantity),
		}))
		.filter((l) => l.quantity > 0)
}

export function Lot() {
	const { id = '' } = useParams()
	const navigate = useNavigate()
	const trpc = useMobileTrpc()
	const { t } = useTranslate()
	const user = useAuthStore((s) => s.user)
	const {
		data: lot,
		isLoading,
		isError,
	} = useQuery(trpc.lots.GetById.queryOptions({ id }))
	// Unified selection: inventoryId -> quantity (always 1 for a slot line).
	const [selection, setSelection] = useState<Map<string, number>>(
		() => new Map(),
	)
	const [purchaseOutcome, setPurchaseOutcome] =
		useState<PurchaseOutcome | null>(null)
	const tezId = user.tezId
	const canOpenAdminLot = hasAdminPermission(user, 'lots_admin', 'seller')
	const [tezDialogOpen, setTezDialogOpen] = useState(false)
	const [pendingSelection, setPendingSelection] = useState<Map<
		string,
		number
	> | null>(null)
	const purchaseRef = useRef<HTMLDivElement>(null)

	const inventory = lot?.inventory
	// Reconcile the stored selection against the freshest inventory on every
	// render (a refetch may have taken a slot or drained stock), then derive
	// everything the UI shows from that — no effect syncing state back to state.
	const lines = lot ? buildLines(selection, lot.inventory) : []
	const reconciledMap = new Map(lines.map((l) => [l.inventoryId, l.quantity]))
	const totalSelected = lines.reduce((sum, l) => sum + l.quantity, 0)
	const selectedSlotIds = new Set(lines.map((l) => l.inventoryId))
	// A draft lot is the initiator's own — invisible in the feed, so in practice
	// only they can open it, and it must look and behave like a normal collecting
	// lot (no state pill, inventory selectable). The backend Buy guard is the
	// real enforcement.
	const canBuy = lot?.state === 'collecting' || lot?.state === 'draft'

	// tezId is required before a purchase. Gate any selection change behind the
	// dialog when it is missing, applying the intended selection on success.
	function applySelection(next: Map<string, number>) {
		setPurchaseOutcome(null)
		if (!tezId) {
			setPendingSelection(next)
			setTezDialogOpen(true)
			return
		}
		setSelection(next)
	}

	function handleSelectSlot(slotId: string) {
		const next = new Map(reconciledMap)
		if (next.has(slotId)) next.delete(slotId)
		else next.set(slotId, 1)
		applySelection(next)
	}

	function handleSetQuantity(itemId: string, quantity: number) {
		if (inventory?.type !== 'stock') return
		const item = inventory.items.find((i) => i.id === itemId)
		if (!item) return
		const clamped = Math.max(0, Math.min(quantity, item.availableQuantity))
		const next = new Map(reconciledMap)
		if (clamped <= 0) next.delete(itemId)
		else next.set(itemId, clamped)
		applySelection(next)
	}

	// When something is picked, reveal the inline purchase panel and scroll to it
	// (the buyer leaves to their bank app and back — no modal to lose). Instant,
	// not smooth: smooth scroll is a no-op in some Telegram webviews. Called
	// directly in the effect (post-layout) — deferring via rAF breaks the scroll
	// in some webviews.
	useEffect(() => {
		if (totalSelected > 0)
			purchaseRef.current?.scrollIntoView({ block: 'start' })
	}, [totalSelected])

	return (
		<Page>
			{isLoading && (
				<div className='flex justify-center py-12'>
					<Spinner size='l' />
				</div>
			)}

			{isError && (
				<Placeholder
					header={t('pages.lot.notFound')}
					description={t('pages.lot.mayBeClosed')}
				/>
			)}

			{lot && inventory && (
				<div className='pb-8'>
					<div className='relative'>
						<LotImageSlider
							photoUrls={lot.photoUrls}
							alt={lot.title}
							imgClassName='aspect-[4/5]'
						/>
						{!canBuy && (
							<span className='absolute left-3 top-3 rounded-full bg-black/65 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm'>
								{t(LOT_STATE_KEYS[lot.state])}
							</span>
						)}
						<div className='absolute right-3 top-3 flex gap-2'>
							<ShareLotButton lotId={lot._id} title={lot.title} />
							{canOpenAdminLot && (
								<button
									type='button'
									className='flex h-9 w-9 items-center justify-center rounded-full bg-black/65 text-white backdrop-blur-sm active:opacity-60'
									onClick={() => navigate(`/admin/lots/${lot._id}`)}
								>
									<ExternalLink className='h-5 w-5' />
								</button>
							)}
						</div>
					</div>
					<div className='space-y-4 p-4'>
						<div>
							<h1 className='text-xl font-semibold text-foreground'>
								{lot.title}
							</h1>
							<div className='mt-1 flex items-end justify-between gap-2'>
								<p className='text-2xl font-bold text-foreground'>
									{t('common.amountUzs', {
										amount: formatUzsAmount(lot.priceUzs),
									})}
								</p>
								<div className='flex flex-col items-end text-xs text-muted-foreground'>
									<p>
										{t('pages.lot.createdAt', {
											date: formatDateTime(lot.createdAt),
										})}
									</p>
									<button
										type='button'
										className='active:text-foreground'
										onClick={() =>
											void navigator.clipboard?.writeText(
												String(lot.orderNumber),
											)
										}
									>
										{t('common.orderNumber', {
											number: String(lot.orderNumber),
										})}
									</button>
								</div>
							</div>
						</div>

						{lot.description && (
							<div className='whitespace-pre-wrap rounded-lg bg-muted p-3 text-sm text-foreground'>
								{lot.description}
							</div>
						)}

						{inventory.type === 'slots' ? (
							<>
								<Banner type='section' header={t('pages.lot.chooseSize')}>
									{t('pages.lot.chooseSizeDescription')}
								</Banner>
								<SizeGrid
									slots={inventory.items}
									selectedSlotIds={selectedSlotIds}
									disabled={!canBuy}
									onSelect={handleSelectSlot}
								/>
							</>
						) : (
							<>
								<Banner type='section' header={t('pages.lot.chooseQuantity')}>
									{t('pages.lot.chooseQuantityDescription')}
								</Banner>
								<StockCounters
									items={inventory.items}
									quantities={reconciledMap}
									disabled={!canBuy}
									onChange={handleSetQuantity}
								/>
							</>
						)}

						{(totalSelected > 0 || purchaseOutcome) && (
							<div ref={purchaseRef} className='scroll-mt-4'>
								<PurchasePanel
									key={lines
										.map((l) => `${l.inventoryId}:${l.quantity}`)
										.sort()
										.join('|')}
									lotId={lot._id}
									lotTitle={lot.title}
									lines={lines}
									unitPriceUzs={lot.priceUzs}
									outcome={purchaseOutcome}
									onPurchaseOutcome={(outcome) => {
										if (outcome.type === 'success') setSelection(new Map())
										setPurchaseOutcome(outcome)
									}}
									onOutcomeDismiss={() => setPurchaseOutcome(null)}
								/>
							</div>
						)}

						{tezDialogOpen && (
							<TezIdDialog
								open
								onOpenChange={setTezDialogOpen}
								onSuccess={() => {
									if (pendingSelection)
										setSelection(
											reconcileSelection(pendingSelection, lot.inventory),
										)
									setPendingSelection(null)
								}}
							/>
						)}
					</div>
				</div>
			)}
		</Page>
	)
}
